import { useMemo } from "react";
import { useWfhWeeklySummary } from "@/api/queries";
import { CategoryBarChart, StatTile, TrendChart } from "@/components/charts";
import { formatDate } from "@/lib/format";

/**
 * How much this team (or this org) is working from home.
 *
 * One component, used on the manager overview, the HR overview and the work-from-
 * home tab of both leave pages, over ONE endpoint that scopes itself by role.
 * The alternative — each screen deriving its own counts from its own request
 * list — is how a manager's number and HR's number end up disagreeing about the
 * same team, with nothing on screen to say which is wrong.
 *
 * Two questions, two charts, deliberately not merged:
 *
 *   WHO   per person, this week (Monday to Sunday). The actionable one: it is
 *         what tells an approver that one person is on four days a week while
 *         the rest of the team is on none.
 *
 *   HOW MUCH, OVER TIME  the whole group, per week, for the last eight weeks.
 *         A single week says nothing about whether anything is changing.
 *
 * Both count APPROVED days only, and working days only — a Friday-to-Monday
 * request is two days, not four, exactly as leave is counted.
 */
export function WfhWeekCharts({ scopeLabel }: { scopeLabel: string }) {
  const summary = useWfhWeeklySummary();

  const people = summary.data?.people ?? [];
  const weeks = summary.data?.weeks ?? [];
  const week = summary.data?.week ?? null;

  // Only people with a day this week get a bar. Everyone in scope comes back
  // from the API, but a column of thirty empty bars is not a chart — the
  // question "did anyone at all" is answered by the empty state below, which
  // says so in words rather than leaving it to be inferred from blank space.
  const thisWeek = useMemo(
    () =>
      people
        .filter((person) => person.days > 0)
        .sort((a, b) => b.days - a.days || (a.name ?? "").localeCompare(b.name ?? ""))
        .map((person) => ({ label: person.name ?? "Unnamed", value: person.days })),
    [people],
  );

  // Eight weeks of zeroes plots as a flat line on the baseline, which reads as a
  // chart that failed to load. When there is genuinely nothing, the empty state
  // says so in words instead.
  const trend = useMemo(
    () =>
      weeks.some((entry) => entry.days > 0)
        ? weeks.map((entry) => ({
            week: formatDate(entry.start, "d MMM") ?? entry.start,
            days: entry.days,
          }))
        : [],
    [weeks],
  );

  const daysThisWeek = thisWeek.reduce((sum, entry) => sum + entry.value, 0);
  const weekNote = week
    ? `${formatDate(week.start, "d MMM")} to ${formatDate(week.end, "d MMM")}`
    : "This week";

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile
          label="Days from home this week"
          value={daysThisWeek}
          icon="wfh"
          loading={summary.isLoading}
          meta={weekNote}
        />
        <StatTile
          label="People working from home"
          value={thisWeek.length}
          icon="team"
          loading={summary.isLoading}
          meta={`of ${people.length} in ${scopeLabel}`}
        />
        <StatTile
          label="Days from home, last 8 weeks"
          value={weeks.reduce((sum, entry) => sum + entry.days, 0)}
          icon="calendar-check"
          loading={summary.isLoading}
          meta="Approved, working days only"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <CategoryBarChart
          title="Days from home this week"
          note={`${weekNote} · ${scopeLabel}`}
          data={thisWeek}
          valueHeader="Days"
          loading={summary.isLoading}
          {...(summary.error ? { error: summary.error.message } : {})}
          height={Math.max(180, thisWeek.length * 34)}
          emptyBody={`Nobody in ${scopeLabel} has an approved work-from-home day this week.`}
        />

        <TrendChart
          title="Work from home over time"
          note={`Approved days per week · ${scopeLabel}`}
          data={trend}
          xKey="week"
          series={[{ key: "days", label: "Days from home" }]}
          area
          loading={summary.isLoading}
          {...(summary.error ? { error: summary.error.message } : {})}
          emptyBody="No approved work-from-home days in the last eight weeks."
        />
      </div>
    </div>
  );
}
